import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from '@/ui/common/Icons';
import styles from './styles.module.scss';

/**
 * Radix UIのセレクトボックス
 *
 * //TODO: リファクタが必ず必要
 *
 * @see https://www.radix-ui.com/primitives/docs/components/select
 */
const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectIcon = SelectPrimitive.Icon;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    /** クリアボタンがクリックされたときに呼び出されるコールバック関数 */
    onClear?: () => void;
    /** 値が選択されているかどうかを示すフラグ（クリアボタンの表示制御に使用） */
    hasValue?: boolean;
    /** クリア機能を有効にするかどうかのフラグ */
    clearable?: boolean;
  }
>(({ className, children, onClear, hasValue, clearable, ...props }, ref) => (
  <SelectPrimitive.Trigger ref={ref} className={`${styles.trigger} ${className || ''}`} {...props}>
    {children}
    <div className={styles.triggerIcons}>
      {onClear && hasValue && clearable && (
        <button
          type='button'
          className={styles.clearButton}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
          onPointerDown={e => e.stopPropagation()}
          aria-label='選択をクリア'
        >
          <CrossIcon size='xs' />
        </button>
      )}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon size='xs' className={styles.triggerIcon} />
      </SelectPrimitive.Icon>
    </div>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={`${styles.scrollButton} ${className || ''}`}
    {...props}
  >
    <ChevronUpIcon className={styles.scrollIcon} />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={`${styles.scrollButton} ${className || ''}`}
    {...props}
  >
    <ChevronDownIcon size='xs' />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    /**
     * Viewport の DOM 要素を受け取るコールバック ref
     * ページネーション対応で IntersectionObserver の root として使用する
     */
    viewportRef?: React.RefCallback<HTMLDivElement>;
  }
>(
  (
    {
      className,
      children,
      position = 'popper' as 'item-aligned' | 'popper',
      viewportRef,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={`${styles.content} ${position === 'popper' ? styles.contentPopper : ''} ${className || ''}`}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          ref={viewportRef}
          className={`${styles.viewport} ${position === 'popper' ? styles.viewportPopper : ''}`}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={`${styles.label} ${className || ''}`} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { value: string }
>(({ className, children, value, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={`${styles.item} ${className || ''}`}
    value={value}
    {...props}
  >
    <span className={styles.itemIndicator}>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className={styles.checkIcon} />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={`${styles.separator} ${className || ''}`}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectIcon,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
